import {
  VERIFY_OTP,
  REQUEST_OTP,
  RESEND_OTP,
  LOGIN,
  LOG_OUT,
  RESET_PASSWORD,
  FORGET_VERIFY_OTP
} from '../action-types';
import { UPDATE_NOTIFICATION, SHOW_LOADING, UPDATE_USER, CANDIDATE_PROFILE_RESET } from '../mutations';
import { NOTIFICATION_MESSAGES } from '../constants';
import httpService from '../../network/http-service';
import { handleError } from '../global/actions';
import { resetProfileCache } from '../candidate/actions';
import { removeCookie, removeCookieForCrossOrigin } from '../../utils/cookie-service';
import { API_ENDPOINT } from '../api-action';

export function login(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.login,
        data: payload
      });

      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });

        return response.data;
      }

      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(LOGIN, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export const logOut = () => {
  return (dispatch) => {
    removeCookieForCrossOrigin('veriipro-user');
    removeCookieForCrossOrigin('veriipro-token');
    removeCookie('veriipro-chat-token');
    removeCookie('is_verify');
    removeCookie('redirectPath');
    // VTS-562: clear the profile freshness/in-flight guards and the in-store
    // profile so the next (different) user never short-circuits to this
    // user's cached data (TS-032/TS-033).
    resetProfileCache();
    dispatch({ type: CANDIDATE_PROFILE_RESET });
    dispatch({ type: UPDATE_USER, payload: null });
    dispatch({ type: UPDATE_NOTIFICATION, payload: NOTIFICATION_MESSAGES[LOG_OUT] });
  };
};

export const updateUser = (payload) => {
  return (dispatch) => {
    // VTS-562: a null payload is a logout (e.g. cross-tab session sync) —
    // reset the profile cache/guards and clear the in-store profile so a
    // subsequent different user loads fresh data.
    if (payload === null) {
      resetProfileCache();
      dispatch({ type: CANDIDATE_PROFILE_RESET });
    }
    dispatch({ type: UPDATE_USER, payload: payload });
  };
};

export function forgotPasswordOtpRequest(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.forgot_password_otp_resquest,
        data: payload
      });
      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });
        return;
      }

      dispatch({ type: UPDATE_NOTIFICATION, payload: NOTIFICATION_MESSAGES[REQUEST_OTP].success });
      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(REQUEST_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function forgotPasswordOtpResend(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.forgot_password_otp_resend,
        data: payload
      });
      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });
        return;
      }

      dispatch({ type: UPDATE_NOTIFICATION, payload: NOTIFICATION_MESSAGES[RESEND_OTP].success });
      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(RESEND_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function forgotPasswordOtpVerify(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.forgot_password_otp_verify,
        data: payload
      });
      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });
        return;
      }

      dispatch({
        type: UPDATE_NOTIFICATION,
        payload: NOTIFICATION_MESSAGES[FORGET_VERIFY_OTP].success
      });
      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(VERIFY_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function forgotPasswordReset(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.password_reset,
        data: payload
      });

      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });
        return;
      }
      dispatch({
        type: UPDATE_NOTIFICATION,
        payload: NOTIFICATION_MESSAGES[RESET_PASSWORD].success
      });
      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(RESET_PASSWORD, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function registration(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.register,
        data: payload
      });
      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });
        return;
      }
      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(VERIFY_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function registerOtpVerify(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.register_otp_verify,
        data: payload
      });
      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });
        return;
      }

      dispatch({ type: UPDATE_NOTIFICATION, payload: NOTIFICATION_MESSAGES[VERIFY_OTP].success });
      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(VERIFY_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function registerOtpResend(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.register_otp_resend,
        data: payload
      });
      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });
        return;
      }

      dispatch({ type: UPDATE_NOTIFICATION, payload: NOTIFICATION_MESSAGES[RESEND_OTP].success });
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(RESEND_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function validateAccount(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.validateAccount,
        data: payload
      });
      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });

        return response.data;
      }

      dispatch({
        type: UPDATE_NOTIFICATION,
        payload: { variant: 'success', message: response.data.msg }
      });

      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(RESEND_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function validateEmailChange(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.validateEmailChange,
        data: payload
      });
      if (response?.data.error) {
        dispatch({
          type: UPDATE_NOTIFICATION,
          payload: { variant: 'error', message: response.data.msg }
        });

        return response.data;
      }

      dispatch({
        type: UPDATE_NOTIFICATION,
        payload: { variant: 'success', message: response.data.msg }
      });

      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(RESEND_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function validateInterviewResponse(payload) {
  return async function (dispatch) {
    dispatch({ type: SHOW_LOADING, payload: true });
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.validateInterviewResponse,
        data: payload
      });
      return response?.data;
    } catch (err) {
      if (err.statusCode === 409) throw err;
      dispatch(handleError(RESEND_OTP, err));
    } finally {
      dispatch({ type: SHOW_LOADING, payload: false });
    }
  };
}

export function checkEmailPhone(payload) {
  return async function (dispatch) {
    try {
      const response = await httpService.post({
        action: API_ENDPOINT.verifyEmailPhone,
        data: payload
      });
      return response?.data;
    } catch (err) {
      return false;
    }
  };
}
